import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { TfiZoomIn, TfiZoomOut } from "react-icons/tfi";
import { GrPowerReset } from "react-icons/gr";
import { FiMaximize, FiMinimize } from "react-icons/fi";
import { useState, useRef, useCallback, useEffect } from "react";
import * as DocxPreview from "docx-preview";
import "./FilePreviewDialog.css";
import { navigateToOfficeViewer } from "../../services/wopi.service";

const FilePreviewDialog = ({ file, onClose, docStorePermissions = [] }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const containerRef = useRef(null);
  const transformRef = useRef();
  const docPreviewRef = useRef(null);

  // Detect file type by extension
  const getFileType = (name) => {
    const ext = name?.split(".").pop().toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext))
      return "image";
    if (["pdf"].includes(ext)) return "pdf";
    if (["doc", "docx"].includes(ext)) return "docx";
    return "other";
  };

  const fileType = getFileType(file?.fileName);

  useEffect(() => {
    if (fileType === "wopi" && file?.fileUrl) {
      const urlParams = new URLSearchParams(file.fileUrl.split("?")[1]);
      const key = urlParams.get("key");
      if (key) {
        navigateToOfficeViewer(key);
        onClose();
      }
    }
  }, [fileType, file, onClose]);

  useEffect(() => {
    if (fileType === "pdf" && file?.fileUrl) {
      fetch(file.fileUrl, {
        method: "GET",
        headers: {
          "Accept": "application/pdf",
        },
        cache: "no-store",
      })
        .then((res) => res.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        })
        .catch((err) => {
          console.error("Error loading PDF:", err);
        });
    }
  }, [fileType, file]);

  useEffect(() => {
    if (fileType === "docx" && file?.fileUrl && docPreviewRef.current) {
      fetch(file.fileUrl)
        .then((res) => res.arrayBuffer())
        .then((content) => {
          docPreviewRef.current.innerHTML = "";
          DocxPreview.renderAsync(content, docPreviewRef.current);
        })
        .catch((err) => {
          console.error("Error loading DOCX:", err);
        });
    }
  }, [fileType, file]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current
        ?.requestFullscreen?.()
        .then(() => setIsFullscreen(true))
        .catch((err) => console.error("Error entering fullscreen:", err));
    } else {
      document
        .exitFullscreen?.()
        .then(() => setIsFullscreen(false))
        .catch((err) => console.error("Error exiting fullscreen:", err));
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleZoomIn = useCallback(() => transformRef.current?.zoomIn(), []);
  const handleZoomOut = useCallback(() => transformRef.current?.zoomOut(), []);
  const handleReset = useCallback(
    () => transformRef.current?.resetTransform(),
    []
  );

  return (
    <div
      className={`modal fade show show-modal modal--opacity file-preview-dialog ${
        isFullscreen ? "modal-fullscreen" : ""
      }`}
      tabIndex="-1"
      role="dialog"
      ref={containerRef}
    >
      <div
        className={`modal-dialog ${isFullscreen ? "modal-fullscreen" : ""}`}
        role="document"
      >
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              />
              <h5 className="modal-title">{file?.fileName}</h5>
            </div>

            {(fileType === "image" ||
              fileType === "pdf" ||
              fileType === "docx") && (
              <div className="controls-container">
                {fileType === "image" && (
                  <>
                    <button
                      onClick={handleZoomIn}
                      className="control-button"
                      title="Zoom In"
                    >
                      <TfiZoomIn size={16} />
                    </button>
                    <button
                      onClick={handleZoomOut}
                      className="control-button"
                      title="Zoom Out"
                    >
                      <TfiZoomOut size={16} />
                    </button>
                    <button
                      onClick={handleReset}
                      className="control-button"
                      title="Reset Zoom"
                    >
                      <GrPowerReset size={16} />
                    </button>
                  </>
                )}
                <button
                  onClick={toggleFullscreen}
                  className="control-button"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <FiMinimize size={14} />
                  ) : (
                    <FiMaximize size={14} />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="modal-body">
            {fileType === "image" ? (
              <TransformWrapper
                initialScale={1}
                minScale={0.1}
                maxScale={20}
                wheel={{ step: 0.1 }}
                doubleClick={{ disabled: true }}
                ref={transformRef}
              >
                <TransformComponent
                  wrapperClass="transform-wrapper"
                  contentClass="transform-component"
                >
                  <img
                    src={file.fileUrl}
                    alt={file.fileName}
                    className="preview-image"
                    onLoad={() => setIsImageLoaded(true)}
                  />
                </TransformComponent>
              </TransformWrapper>
            ) : fileType === "pdf" ? (
              <div className="pdf-preview">
                <iframe
                  src={pdfUrl}
                  title={file.fileName}
                  className="preview-iframe"
                />
              </div>
            ) : fileType === "docx" ? (
              <div className="docx-preview" ref={docPreviewRef} />
            ) : (
              <div className="no-preview-container">
                <div className="no-preview-icon">
                  <i className="far fa-file"></i>
                </div>
                <p className="no-preview-text">
                  No preview available for this file type. Please download the
                  file to view it.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilePreviewDialog;
